/// <reference types="jquery" />

import Q = require("q");
import SignalR = require("SignalR/Hubs");
import TFS_Host_TfsContext = require("Presentation/Scripts/TFS/TFS.Host.TfsContext");
import { ScopedEventHelper } from "Agile/Scripts/ScopedEventHelper";
import { Events } from "Agile/Scripts/SprintsHub/Taskboard/AutoRefresh/TaskboardAutoRefreshConstants";
import { PublishAutoRefreshTelemetry } from "Agile/Scripts/SprintsHub/Taskboard/AutoRefresh/TaskboardAutoRefreshTelemetry";
import { TaskboardWorkItemChangedPayload } from "Agile/Scripts/SprintsHub/Taskboard/AutoRefresh/TaskboardWorkItemChangedPayload";
import { TaskboardAutoRefreshThrottler } from "Agile/Scripts/SprintsHub/Taskboard/AutoRefresh/TaskboardAutoRefreshThrottling";
import { WorkItemTaskboardColumnChangedEvent } from "Agile/Scripts/SprintsHub/Taskboard/AutoRefresh/WorkItemTaskboardColumnChangedEvent";

/**
 * Manages SignalR hub for Taskboard.
 */
export class TaskboardAutoRefreshHub extends SignalR.Hub {
    protected _autoRefreshThrottler: TaskboardAutoRefreshThrottler;
    private _subscribed: boolean = false;

    private _sessionStartTime: Date;
    private _teamId: string;
    private _connectionTimeout: number;
    private _eventsHelper: ScopedEventHelper;

    constructor(teamId: string, eventsHelper: ScopedEventHelper) {
        super(
            TFS_Host_TfsContext.TfsContext.getDefault().contextData.account,
            TFS_Host_TfsContext.TfsContext.getDefault().contextData.collection,
            "taskboardHub",
            null, //connection string or HubConnection
            { useSignalRAppPool: TaskboardAutoRefreshHub.useSignalRAppPool(TFS_Host_TfsContext.TfsContext.getDefault()) }
        );
        this._eventsHelper = eventsHelper;
        this._teamId = teamId;
        this._initializeHub(this.hub);
    }

    private static CONNECTION_TIME_OUT_DELAY_IN_MS = 30000;

    public start(): Q.Promise<any> {
        if (this._subscribed) {
            return this._unsubscribe().then(() => {
                return this._subscribe();
            });
        } else {
            return this._subscribe();
        }
    }

    /**
     * Called when we want to stop the SignalR connection.
     */
    public stop() {
        this.publishSignalRSessionLength();
        this._clearTimeout();
        this._teamId = null;
        this._subscribed = false;
        if (this._autoRefreshThrottler) {
            this._autoRefreshThrottler.dispose();
            this._autoRefreshThrottler = null;
        }
        super.stop();
    }

    public publishSignalRSessionLength(): void {
        if (this._sessionStartTime) {
            PublishAutoRefreshTelemetry(
                "SignalRConnection",
                this._teamId,
                {
                    ConnectionState: "Disconnected",
                    SessionLength: new Date().getTime() - this._sessionStartTime.getTime()
                },
                true
            );
            this._sessionStartTime = null;
        }
    }

    protected onDisconnect(): void {
        if (this._teamId) {
            // Raise event when signalR pipeline is disconnected (e.g. network outage).
            this._eventsHelper.fire(Events.SignalRDisconnected, this);

            PublishAutoRefreshTelemetry("SignalRConnection", this._teamId, {
                ConnectionState: "Disconnected"
            });

            this._clearTimeout();
            this._subscribed = false;
            this._teamId = null;

        }
        return super.onDisconnect();
    }

    /**
     * Initialize the hub.
     */
    private _initializeHub(hub: any) {
        hub.client.onWorkItemUpdated = (updatePayload: TaskboardWorkItemChangedPayload) => {
            this._autoRefreshThrottler.process([updatePayload]);
        };

        hub.client.onWorkItemColumnUpdated = (payload: WorkItemTaskboardColumnChangedEvent) => {
            this._eventsHelper.fire(Events.WorkItemTaskboardColumnChanged, this, payload);
        }

        hub.client.onTaskboardColumnOptionsChanged = () => {
            this._eventsHelper.fire(Events.TaskboardColumnOptionsChanged, this);
        }

        hub.client.onTaskboardCardSettingsChanged = () => {
            this._eventsHelper.fire(Events.TaskboardCardSettingsChanged, this);
        }

        hub.client.onTeamSettingsChanged = () => {
            this._eventsHelper.fire(Events.TeamSettingsChanged, this);
        }
    }

    private _subscribe(): Q.Promise<any> {
        let deferred = Q.defer<any>();

        this._connectionTimeout = setTimeout(() => {
            // If the connection start takes more the the conenction timeout, stop the connection
            this.stop();
        }, this._getConnectionTimeoutInMS());

        this.connection
            .start()
            .then((hub: SignalR.HubConnection) => {
                this._clearTimeout();
                if (hub) {
                    this._autoRefreshThrottler = new TaskboardAutoRefreshThrottler(this._teamId, this._eventsHelper);
                    this._sessionStartTime = new Date();
                    this._subscribed = true;
                    Q(this.hub.server.watchTaskboard(this._teamId)).then(() => {
                        PublishAutoRefreshTelemetry("SignalRConnection", this._teamId, {
                            ConnectionState: "Connected"
                        });
                        deferred.resolve(hub);
                    });
                } else {
                    // Case due to our timeout
                    deferred.reject({
                        name: "Taskboard.AutoRefresh.Hub.Connection.Start.Timeout",
                        message: "AutoRefresh connection start call failed due to time out"
                    });
                }
            })
            // @ts-ignore Really a q-promise.
            .fail((error: Error) => {
                deferred.reject(error);
            });

        return deferred.promise;
    }

    private _unsubscribe(): Q.Promise<any> {
        this._clearTimeout();
        return Q(this.hub.server.unwatchTaskboard(this._teamId)).then(() => {
            this._subscribed = false;
        });
    }

    private _clearTimeout(): void {
        if (this._connectionTimeout) {
            clearTimeout(this._connectionTimeout);
            this._connectionTimeout = null;
        }
    }

    /**
     * Returns the timeout delay in MS.
     * Introducing a private for the purpose of testing
     */
    private _getConnectionTimeoutInMS(): number {
        return TaskboardAutoRefreshHub.CONNECTION_TIME_OUT_DELAY_IN_MS;
    }
}
